import { Color, Group, Vector3 } from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { PartSharpEdges, SharpEdge } from '@infinitform/platform-core';
import type { Engine } from '../engine';
import { PartType } from '../../models/enums';

/** Reads a part's sharp edges from the project; null when it has none on file. */
export type SharpEdgesLoader = (partId: string) => Promise<PartSharpEdges | null>;

/**
 * The angle the optimizer treats as sharp. Import measures past it, so edges
 * just above it are drawn as well, in their own colour.
 */
export const SHARP_EDGE_THRESHOLD_DEG = 88;

/**
 * A part with an edge below SHARP_EDGE_RAISE_BELOW_DEG is treated as sharp up
 * to SHARP_EDGE_RAISED_THRESHOLD_DEG instead, as brepsmith's sharpedges does
 * (--se-raise-below, --se-raised-threshold).
 */
export const SHARP_EDGE_RAISE_BELOW_DEG = 60;
export const SHARP_EDGE_RAISED_THRESHOLD_DEG = 95;

/** The threshold the optimizer applies to this part. */
export function partThresholdDeg(data: PartSharpEdges): number {
  const sharpest = Math.min(...data.edges.map((edge) => edge.minAngleDeg));
  return sharpest < SHARP_EDGE_RAISE_BELOW_DEG ? SHARP_EDGE_RAISED_THRESHOLD_DEG : SHARP_EDGE_THRESHOLD_DEG;
}

// Red for sharp, blue for just above it: both stand out on the orange design
// parts and from each other.
const SHARP_COLOR = new Color('#e5484d');
const NEAR_SHARP_COLOR = new Color('#2563eb');

// Wider than a selected part edge, so the overlay reads over it.
const LINE_WIDTH_CSS_PX = 4;

// Label backgrounds: the line colours darkened until white text on them is
// above 7:1.
const SHARP_LABEL_BG = '#a1161b';
const NEAR_SHARP_LABEL_BG = '#1e40af';

/** The point halfway along a polyline, by length. */
function midpoint(points: SharpEdge['points']): Vector3 {
  const lengths: number[] = [];
  let total = 0;
  for (let i = 0; i + 1 < points.length; i++) {
    const [ax, ay, az] = points[i];
    const [bx, by, bz] = points[i + 1];
    const length = Math.hypot(bx - ax, by - ay, bz - az);
    lengths.push(length);
    total += length;
  }
  let remaining = total / 2;
  for (let i = 0; i < lengths.length; i++) {
    if (remaining <= lengths[i] && lengths[i] > 0) {
      const t = remaining / lengths[i];
      const a = new Vector3(...points[i]);
      return a.lerp(new Vector3(...points[i + 1]), t);
    }
    remaining -= lengths[i];
  }
  return new Vector3(...points[0]);
}

/** Angle between the two faces at point i, from their normals; the edge's own minimum when they are missing. */
function angleAt(edge: SharpEdge, i: number): number {
  const a = edge.n1?.[i];
  const b = edge.n2?.[i];
  if (!a || !b) return edge.minAngleDeg;
  const dot = Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  return 180 - (Math.acos(dot) * 180) / Math.PI;
}

/**
 * Admin overlay: the sharp edges of every design part, as measured at import.
 *
 * Each overlay is a child of its part's group, so it follows the part through
 * visibility, the UCS turn and the display frame exactly as the part's own
 * edges do. The set follows the design parts: switching a part to or from
 * design adds or drops its overlay.
 *
 * Nothing is read until a loader is set. From then on the manager keeps track
 * of whether any design part has sharp edges on file, which is what decides
 * whether the overlay is offered at all.
 */
export class SharpEdgesManager {
  private shown = false;
  private loader: SharpEdgesLoader | null = null;
  private readonly lines = new Map<string, Group>();
  private readonly loaded = new Map<string, PartSharpEdges | null>();
  private readonly loading = new Map<string, Promise<PartSharpEdges | null>>();
  private refreshQueued = false;
  private hasEdges = false;

  /** Called whenever {@link available} changes. */
  onAvailabilityChange: ((available: boolean) => void) | null = null;

  constructor(private readonly engine: Engine) {}

  get visible(): boolean {
    return this.shown;
  }

  /** Whether any design part has sharp edges on file. */
  get available(): boolean {
    return this.hasEdges;
  }

  /** Where the parts' edges are read from. Re-reads them and re-checks availability. */
  setLoader(loader: SharpEdgesLoader): void {
    this.loader = loader;
    this.loaded.clear();
    this.loading.clear();
    this.refresh();
  }

  /** Shows the overlay for the design parts that have sharp edges. */
  async show(): Promise<void> {
    this.shown = true;
    await this.sync();
  }

  hide(): void {
    this.shown = false;
    for (const partId of [...this.lines.keys()]) this.removeLine(partId);
    this.engine.requestRender();
  }

  /** Re-checks availability and the overlay against the current design parts, once per tick. */
  refresh(): void {
    if (!this.loader || this.refreshQueued) return;
    this.refreshQueued = true;
    queueMicrotask(() => {
      this.refreshQueued = false;
      void this.update();
    });
  }

  private async update(): Promise<void> {
    await this.checkAvailability();
    if (this.shown) await this.sync();
  }

  private async checkAvailability(): Promise<void> {
    const design = this.engine.objects.filter((o) => o.type === PartType.DESIGN).map((o) => String(o.data.id));
    const data = await Promise.all(design.map((partId) => this.load(partId)));
    const available = data.some((d) => !!d && d.edges.length > 0);
    if (available === this.hasEdges) return;
    this.hasEdges = available;
    // Nothing left to show: the overlay goes off with the switch.
    if (!available && this.shown) this.hide();
    this.onAvailabilityChange?.(available);
  }

  private async sync(): Promise<void> {
    const design = new Map(
      this.engine.objects.filter((o) => o.type === PartType.DESIGN).map((o) => [String(o.data.id), o])
    );

    for (const partId of [...this.lines.keys()]) {
      const line = this.lines.get(partId)!;
      // Gone from design, or its part was rebuilt and the overlay went with the old group.
      if (!design.has(partId) || line.parent !== design.get(partId)!.group) this.removeLine(partId);
    }

    await Promise.all(
      [...design].map(async ([partId, obj]) => {
        if (this.lines.has(partId)) return;
        const data = await this.load(partId);
        // The overlay may have been hidden, or the part changed, while this was read.
        if (!this.shown || this.lines.has(partId) || obj.type !== PartType.DESIGN) return;
        if (!data || data.edges.length === 0) return;
        const line = this.buildLine(data);
        if (line.children.length === 0) return;
        obj.group.add(line);
        this.lines.set(partId, line);
      })
    );
    this.engine.requestRender();
  }

  private load(partId: string): Promise<PartSharpEdges | null> {
    if (this.loaded.has(partId)) return Promise.resolve(this.loaded.get(partId)!);
    let pending = this.loading.get(partId);
    if (!pending) {
      const loader = this.loader;
      pending = (loader ? loader(partId) : Promise.resolve(null))
        .catch(() => null)
        .then((data) => {
          this.loaded.set(partId, data);
          this.loading.delete(partId);
          return data;
        });
      this.loading.set(partId, pending);
    }
    return pending;
  }

  // Wrapped in a group, as the part's own edges are: LineSegments2 is a Mesh,
  // and the hover outline takes every Mesh directly under the part's group.
  // Each segment takes the colour of its sharper end.
  private buildLine(data: PartSharpEdges): Group {
    const threshold = partThresholdDeg(data);
    const sharp: number[] = [];
    const nearSharp: number[] = [];
    for (const edge of data.edges) {
      for (let i = 0; i + 1 < edge.points.length; i++) {
        const angle = Math.min(angleAt(edge, i), angleAt(edge, i + 1));
        (angle < threshold ? sharp : nearSharp).push(...edge.points[i], ...edge.points[i + 1]);
      }
    }

    const holder = new Group();
    holder.name = 'sharp-edges';
    if (sharp.length) holder.add(this.segments(sharp, SHARP_COLOR, 'sharp'));
    if (nearSharp.length) holder.add(this.segments(nearSharp, NEAR_SHARP_COLOR, 'near-sharp'));
    if (holder.children.length) {
      for (const edge of data.edges) holder.add(this.label(edge, threshold));
    }
    return holder;
  }

  /** The edge's sharpest angle, over the middle of the edge. */
  private label(edge: SharpEdge, threshold: number): CSS2DObject {
    const div = document.createElement('div');
    div.className = 'sharp-edge-label';
    div.textContent = `${edge.minAngleDeg.toFixed(1)}°`;
    Object.assign(div.style, {
      background: edge.minAngleDeg < threshold ? SHARP_LABEL_BG : NEAR_SHARP_LABEL_BG,
      color: 'white',
      padding: '0.125rem 0.375rem',
      borderRadius: '0.1875rem',
      fontSize: 'var(--font-size-sm, 0.75rem)',
      fontWeight: '600',
      lineHeight: '1.2',
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    });
    const label = new CSS2DObject(div);
    label.name = 'sharp-edge-label';
    label.position.copy(midpoint(edge.points));
    // Just above the edge rather than on it, so the line stays visible.
    label.center.set(0.5, 1.4);
    return label;
  }

  private segments(positions: number[], color: Color, name: string): LineSegments2 {
    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(positions);

    // The part-edge material, so it keeps its width through resizes and
    // clipping, in its own colour and width.
    const material = this.engine.createWireLineMaterial();
    material.color.copy(color);
    material.userData['lineWidthCssPx'] = LINE_WIDTH_CSS_PX;
    material.linewidth = this.engine.materialLineWidth(material);

    const line = new LineSegments2(geometry, material);
    line.computeLineDistances();
    line.name = name;
    line.renderOrder = 1;
    // Not pickable: hovering or clicking the part must still reach its faces.
    line.raycast = () => {};
    return line;
  }

  private removeLine(partId: string): void {
    const holder = this.lines.get(partId);
    if (!holder) return;
    holder.removeFromParent();
    for (const child of [...holder.children]) {
      if (child instanceof LineSegments2) {
        child.geometry.dispose();
        child.material.dispose();
      } else if (child instanceof CSS2DObject) {
        // A label's element leaves the page only when the label itself is
        // removed; taking the holder away leaves it drawn where it was.
        child.removeFromParent();
        child.element.remove();
      }
    }
    this.lines.delete(partId);
  }
}
